import { useId, useRef } from "react";
import type { KeyboardEvent, ReactNode } from "react";
import { classNames } from "./internal/class-names";

export interface TabDefinition {
  readonly id: string;
  readonly label: ReactNode;
  readonly disabled?: boolean | undefined;
  /**
   * How many items wait behind this tab. Rendered as a badge and left in the
   * tab's accessible name on purpose — "Orders 3" is the useful announcement,
   * and hiding the number from screen readers would withhold the one fact
   * that decides where an operator looks next.
   */
  readonly count?: number | undefined;
}

export interface TabsProps {
  /** Accessible name of the tab list. */
  readonly label: string;
  readonly tabs: readonly TabDefinition[];
  readonly activeId: string;
  readonly onChange: (id: string) => void;
  /**
   * `automatic` selects the tab that receives focus (APG default, right when
   * panels are cheap). `manual` moves focus only; Enter or Space selects.
   */
  readonly activation?: "automatic" | "manual" | undefined;
  readonly orientation?: "horizontal" | "vertical" | undefined;
  readonly className?: string | undefined;
  /** Panel content for the active tab. The caller decides what to mount. */
  readonly children: ReactNode;
}

function nextEnabledIndex(
  tabs: readonly TabDefinition[],
  from: number,
  step: number,
): number {
  const count = tabs.length;
  if (count === 0) return from;
  for (let offset = 1; offset <= count; offset += 1) {
    const index = (from + step * offset + count * count) % count;
    if (tabs[index]?.disabled !== true) return index;
  }
  return from;
}

function firstEnabledIndex(tabs: readonly TabDefinition[], step: number): number {
  const start = step > 0 ? 0 : tabs.length - 1;
  return tabs[start]?.disabled === true ? nextEnabledIndex(tabs, start, step) : start;
}

/**
 * Tab list with a full roving tabindex: exactly one tab is in the tab order and
 * arrow keys, Home and End move between them.
 *
 * This component and `Dialog` are the two the project would hand to a headless
 * primitive library once that amendment is approved; the props above are the
 * contract that must survive such a swap.
 */
export function Tabs({
  activation = "automatic",
  activeId,
  children,
  className,
  label,
  onChange,
  orientation = "horizontal",
  tabs,
}: TabsProps): ReactNode {
  const generatedId = useId();
  const listRef = useRef<HTMLDivElement | null>(null);
  const activeIndex = tabs.findIndex((tab) => tab.id === activeId);
  const tabId = (id: string): string => `${generatedId}-tab-${id}`;
  const panelId = (id: string): string => `${generatedId}-panel-${id}`;

  function focusTab(index: number): void {
    const tab = tabs[index];
    if (tab === undefined) return;
    const button = listRef.current?.children.item(index);
    if (button instanceof HTMLElement) button.focus();
    if (activation === "automatic") onChange(tab.id);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const previousKey = orientation === "vertical" ? "ArrowUp" : "ArrowLeft";
    const nextKey = orientation === "vertical" ? "ArrowDown" : "ArrowRight";
    const current = activeIndex < 0 ? 0 : activeIndex;

    if (event.key === previousKey) {
      event.preventDefault();
      focusTab(nextEnabledIndex(tabs, current, -1));
    } else if (event.key === nextKey) {
      event.preventDefault();
      focusTab(nextEnabledIndex(tabs, current, 1));
    } else if (event.key === "Home") {
      event.preventDefault();
      focusTab(firstEnabledIndex(tabs, 1));
    } else if (event.key === "End") {
      event.preventDefault();
      focusTab(firstEnabledIndex(tabs, -1));
    }
  }

  return (
    <div
      className={classNames(
        "thw-tabs",
        orientation === "vertical" && "thw-tabs--vertical",
        className,
      )}
    >
      <div
        aria-label={label}
        aria-orientation={orientation}
        className="thw-tabs__list"
        onKeyDown={handleKeyDown}
        ref={listRef}
        role="tablist"
      >
        {tabs.map((tab) => {
          const selected = tab.id === activeId;
          return (
            <button
              aria-controls={panelId(tab.id)}
              aria-disabled={tab.disabled === true ? "true" : undefined}
              aria-selected={selected}
              className="thw-tab"
              id={tabId(tab.id)}
              key={tab.id}
              onClick={() => {
                if (tab.disabled !== true) onChange(tab.id);
              }}
              role="tab"
              tabIndex={selected ? 0 : -1}
              type="button"
            >
              <span className="thw-tab__label">{tab.label}</span>
              {tab.count === undefined ? null : (
                <span className="thw-tab__count">{tab.count}</span>
              )}
            </button>
          );
        })}
      </div>
      <div
        aria-labelledby={activeIndex < 0 ? undefined : tabId(activeId)}
        className="thw-tabs__panel"
        id={panelId(activeId)}
        role="tabpanel"
        tabIndex={0}
      >
        {children}
      </div>
    </div>
  );
}
